import React, { useContext, useMemo } from "react";
import Group from "./Group";
import { HintAndTerms } from "./HintAndTerms";
import { FormContext } from "./context";
import { StatusCard, StatusCardProps } from "../StatusCard";
import { PanelColor, ThemeColor } from "../../props";

export interface Field {
  /** Unique identifier for the field */
  id: string;
  /** Field type (TEXT, TEXTAREA, INTEGER, DATE, DATETIME, TIME, BOOL, SELECT) */
  type: string;
  /** Display label for the field */
  label: string;
  /** Current value of the field */
  value: any;
  /** Whether the field is required */
  required?: boolean;
  /** Group name for organizing fields into sections */
  group?: string;
  /** Whether the field is hidden */
  hidden?: boolean;
  /** Whether this field should receive autofocus */
  autoFocus?: boolean;
  /** Options for SELECT type fields */
  options?: Array<{ value: any; label: string }>;
  /** Helper text to display below the field */
  helper?: string;
  /** Error message to display */
  error?: string;
  /** Additional field context */
  context?: string;
  [key: string]: any;
}

export interface FormFieldsProps {
  /** Array of field definitions */
  fields: Field[] | null;
  /** Callback when a field value changes */
  onChangeField: (id: string, value: any, opts?: any) => void;
  /**
   * Content shown when there are no visible fields.
   * Omit to use the default `StatusCard` (customize it via `noFieldsProps`),
   * pass a node to fully replace it, or pass `false` to render nothing.
   */
  noFields?: React.ReactNode;
  /** Props merged onto the default empty-state `StatusCard` when `noFields` isn't overridden */
  noFieldsProps?: Partial<StatusCardProps>;
  /** Whether to autofocus the first field */
  autoFocus?: boolean;
  /** Whether to show hint and terms section */
  showHintAndTerms?: boolean;
  /** All form states from useMultiForm — lets fields access sibling form data */
  multiForms?: any[];
  /** Active preset object from useMultiForm for this form */
  preset?: Record<string, any>;
  onLinkFieldPreset?: (fieldId: string) => void;
  onUnlinkFieldPreset?: (fieldId: string, value?: any, data?: any) => void;
  onChangePresetField?: (fieldId: string, value: any, data: any) => void;
  disabled?: boolean;
  [key: string]: any;
}

function InnerFields({
  fields = [],
  noFields,
  noFieldsProps,
  autoFocus,
  showHintAndTerms = true,
  ...props
}: FormFieldsProps) {
  const groups = useMemo(
    () =>
      fields.reduce(
        (acc, f) => (acc.includes(f.group) ? acc : [...acc, f.group]),
        [],
      ),
    [fields],
  );

  const autoFocusField = fields.find((f) => f.autoFocus);

  if (fields.some((f) => !f.hidden))
    return (
      <>
        {groups.map((group, idx) => (
          <Group
            {...props}
            key={idx}
            autoFocus={
              autoFocus && autoFocusField?.group === group ? true : idx === 0
            }
            {...{ group, fields }}
          />
        ))}
        {showHintAndTerms && <HintAndTerms {...props} {...{ fields }} />}
      </>
    );

  return (
    noFields ?? (
      <div className="p-5">
        <StatusCard
          dense
          color={ThemeColor.none}
          title={{ local: "NoFields" }}
          panelProps={{color: PanelColor.level0}}
          subtitle={null}
          {...noFieldsProps}
        />
      </div>
    )
  );
}

/**
 * Renders form fields based on the field definitions provided by useForm hook.
 * Automatically groups fields and handles layout based on the Form's variant prop.
 *
 * @example
 * ```tsx
 * const { fields, changeField } = useForm(initialFields);
 *
 * <Form>
 *   <Form.Fields fields={fields} onChangeField={changeField} />
 * </Form>
 * ```
 */
export function FormFields(props: FormFieldsProps) {
  const { variant } = useContext(FormContext) as any;

  if (variant === "narrow")
    return (
      <div className="flex flex-col">
        <InnerFields {...props} />
      </div>
    );

  return (
    <div className="flex justify-center">
      <div className="basis-[800px]">
        <InnerFields {...props} />
      </div>
    </div>
  );
}
